import React, { useState, useEffect, useCallback } from 'react';
import styles from './SurveyRequest.module.scss';
import Breadcrumbs from '../../../components/breadcrumbs/Breadcrumbs';
import RadioButton from '../../../components/radioButton/RadioButton';
import Button from '../../../components/button/Button';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import { useNavigate, useParams } from 'react-router-dom';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';

const SurveyRequest = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [request, setRequest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});

  const fetchRequest = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axiosClient.get(`/surveys/requests/${requestId}`);
      setRequest(response.data?.request || response.data || response);
    } catch (error) {
      showToast(error.message || 'Failed to load survey', 'error');
      navigate('/surveys');
    } finally {
      setIsLoading(false);
    }
  }, [requestId, showToast, navigate]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  const handleSelect = (questionId, optionId) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
    if (errors[questionId]) {
      setErrors((prev) => ({ ...prev, [questionId]: false }));
    }
  };

  const handleSubmit = async () => {
    const questions = request?.survey?.questions || [];
    const newErrors = {};
    questions.forEach((q) => {
      if (!answers[q.id]) newErrors[q.id] = true;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showToast('Please answer all questions before submitting', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        answers: Object.entries(answers).map(([questionId, optionId]) => ({
          questionId: parseInt(questionId),
          optionId,
        })),
      };
      await axiosClient.post(`/surveys/requests/${requestId}/submit`, payload);
      showToast('Survey submitted successfully');
      navigate('/surveys');
    } catch (error) {
      showToast(error.message || 'Failed to submit survey', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contextLabel =
    request?.type === 'Instructor'
      ? `${request.instructor?.firstName || ''} ${request.instructor?.lastName || ''}`.trim()
      : request?.clinicalSite?.name || '';

  const questions = request?.survey?.questions || [];

  return (
    <>
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Surveys', path: '/surveys' },
            { label: request?.survey?.title || 'Survey' },
          ]}
        />
      </div>
      <div className={styles.container}>
        {(isLoading || isSubmitting) && <LoadingOverlay />}
        {request && (
          <>
            <div className={styles.surveyHeader}>
              <h1 className={styles.surveyTitle}>{request.survey?.title}</h1>
              {contextLabel && (
                <p className={styles.contextLabel}>{contextLabel}</p>
              )}
            </div>

            <div className={styles.questions}>
              {questions.map((question, index) => (
                <div
                  key={question.id}
                  className={`${styles.questionCard} ${errors[question.id] ? styles.questionError : ''}`}
                >
                  <p className={styles.questionText}>
                    <span className={styles.questionNumber}>{index + 1}.</span>
                    {question.text}
                  </p>
                  <div className={styles.options}>
                    {question.options.map((option) => (
                      <RadioButton
                        key={option.id}
                        label={option.value}
                        checked={answers[question.id] === option.id}
                        onChange={() => handleSelect(question.id, option.id)}
                      />
                    ))}
                  </div>
                  {errors[question.id] && (
                    <span className={styles.errorText}>This question is required</span>
                  )}
                </div>
              ))}
            </div>

            {questions.length > 0 && (
              <div className={styles.actions}>
                <Button
                  label="Cancel"
                  variant="white"
                  size="medium"
                  onClick={() => navigate('/surveys')}
                />
                <Button
                  label="Submit Survey"
                  variant="primary"
                  size="medium"
                  onClick={handleSubmit}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default SurveyRequest;
